import type {
  // RecaptchaRequestConfig,
  RequestErrorData,
  RequestErrorInfo,
  SignIn,
  AuthToken,
  User,
  ParticipantCreatePayload,
  Participant,
  UserPayload,
  ParticipantScorePayload,
  Address,
  CampaignGameLeaderboardParams,
  CampaignGameLeaderboard,
  FamilyCreatePayload,
  Family,
  SignedUrl,
  AvatarData,
  SignInBody,
  Campaign
} from './types';

import axios from 'axios';
import Store from '@/redux';
// import { reCAPTCHA } from '@/utils/API';
import type { AxiosError } from 'axios';

import { setAuthData } from '@/redux/auth/actions';
import type { UserState } from '@/redux/user/types';
import { setUserData, setUserFamily } from '@/redux/user/actions';
import {
  getAccessExpiration,
  getAccessToken,
  getRefreshExpiration,
  getRefreshToken
} from '@/redux/auth/selectors';
import { setCampaignServerTime } from '@/redux/campaign/actions';
import { getUserState } from '@/redux/user/selectors';
import { getAuthCode } from '@/services/app/mcd';
import { setCampaignData } from '@/redux/campaign/actions';

class APIService {
  private readonly anonymousUrls = ['/auth/token/refresh', '/auth/signin'];
  private readonly requests: Map<string, number> = new Map();

  private readonly axios = axios.create({
    headers: { common: { 'x-family-api-key': import.meta.env.VITE_API_KEY } },
    baseURL: `${import.meta.env.VITE_API_BASE_URL}/api`,
    withCredentials: true
  });

  public constructor() {
    this.createInterceptors();
  }

  private async updateExpiredToken(
    state = Store.getState(),
    now = Date.now()
  ): Promise<void> {
    // Force premature expiration if token will expire in 5 seconds:
    const accessExpiration = (getAccessExpiration(state) ?? now) - 5e3;
    const refreshExpiration = (getRefreshExpiration(state) ?? now) - 5e3;

    if (refreshExpiration <= now) {
      await this.login(getUserState(state));
    } else if (accessExpiration <= now) {
      await this.refreshToken(getRefreshToken(state));
    }
  }

  private createInterceptors(): void {
    this.axios.interceptors.request.use(async (config) => {
      this.updateRequests(config.url, true);

      if (config?.url && !this.anonymousUrls.includes(config.url)) {
        await this.updateExpiredToken();
        const accessToken = getAccessToken(Store.getState());

        if (accessToken && config?.headers) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
      }

      return config;
    });

    this.axios.interceptors.response.use(
      async (response) => {
        Store.dispatch(setCampaignServerTime(response.headers.date));
        this.updateRequests(response.config.url);
        return response;
      },

      async (error) => {
        this.updateRequests(error.config?.url);
        return Promise.reject(error);
      }
    );
  }

  private updateRequests(url = '', add = false): void {
    let pi = url.indexOf('?');
    pi = pi < 0 ? url.length : pi;
    const baseUrl = url.slice(0, pi);

    const current = this.requests.get(baseUrl) ?? 0;
    this.requests.set(baseUrl, Math.max(current + +add * 2 - 1, 0));
  }

  private startedRequests(...requests: string[]): boolean {
    for (let r = requests.length; r--; ) {
      if (this.requests.get(requests[r])) {
        return true;
      }
    }

    return false;
  }

  // private async getReCaptchaConfig(): RecaptchaRequestConfig {
  //   if (!(window as Application).grecaptcha) {
  //     if (import.meta.env.VITE_RECAPTCHA_ENTERPRISE_KEY)
  //       throw new Error('ReCaptcha not initialized.');
  //
  //     return;
  //   }
  //
  //   try {
  //     const { enterprise } = (window as Application).grecaptcha as GreCaptcha;
  //     const token = await enterprise.execute(reCAPTCHA, { action: 'post' });
  //     return { headers: { 'x-recaptcha-token': token } };
  //   } catch {
  //     throw new Error('ReCaptcha not fetched.');
  //   }
  // }

  public getRequestErrorInfo(axiosError: AxiosError): RequestErrorInfo {
    const { status = undefined, statusText = undefined } =
      axiosError.response ?? {};

    const {
      statusCode = undefined,
      message = undefined,
      error = undefined
    } = (axiosError.response?.data as RequestErrorData) ?? {};

    return {
      message: message || axiosError.message,
      code: statusCode || status,
      error: error || statusText
    };
  }

  /**
   * Auth Endpoints:
   */
  public revokeToken(): void {
    Store.dispatch(
      setAuthData({
        refreshExpiresAt: null,
        accessExpiresAt: null,
        refreshToken: null,
        accessToken: null
      })
    );
  }

  private getAccessExpiration(delay = 0): number {
    return Date.now() + delay * 1e3;
  }

  public async login(user: UserState): Promise<void> {
    try {
      if (!this.startedRequests('/auth/signin')) {
        const casAuthToken =
          import.meta.env.VITE_CAS_CLIENT_ID &&
          (await getAuthCode(import.meta.env.VITE_CAS_CLIENT_ID));

        const me = await this.signIn({
          firstName: user.firstname,
          lastName: user.lastname,
          deviceId: user.deviceId,
          email: user.email,
          mcdonaldsId: user.mcdonaldsId,
          casAuthToken
        });

        try {
          await this.setCampaignData(user);
        } catch (error) {
          console.error('Error in setCampaignData:', error);
        }

        Store.dispatch(setUserData({ ...user, ...me }));
      }
    } catch (error) {
      console.error('Error in login:', error);
      throw error;
    }
  }

  private async signIn(data: SignInBody): Promise<User> {
    const { token, me } = (await this.axios.post<SignIn>('/auth/signin', data))
      .data;

    const { accessToken, refreshToken, accessExpiresIn, refreshExpiresIn } =
      token;

    const refreshExpiresAt = this.getAccessExpiration(refreshExpiresIn);
    const accessExpiresAt = this.getAccessExpiration(accessExpiresIn);

    Store.dispatch(
      setAuthData({
        refreshExpiresAt,
        accessExpiresAt,
        refreshToken,
        accessToken
      })
    );

    return me as User;
  }

  private async refreshToken(token: string | null): Promise<AuthToken | void> {
    const { data } = await this.axios.post<AuthToken>('/auth/token/refresh', {
      refreshToken: token
    });

    const { accessToken, refreshToken, accessExpiresIn, refreshExpiresIn } =
      data;

    const refreshExpiresAt = this.getAccessExpiration(refreshExpiresIn);
    const accessExpiresAt = this.getAccessExpiration(accessExpiresIn);

    Store.dispatch(
      setAuthData({
        refreshExpiresAt,
        accessExpiresAt,
        refreshToken,
        accessToken
      })
    );

    return data;
  }
  /**
   * User Endpoints:
   */
  public async getUser(): Promise<User> {
    return (await this.axios.get<User>('/users/me')).data;
  }

  public async createParticipant(
    data: ParticipantCreatePayload
  ): Promise<Participant> {
    if (!this.startedRequests('/participants')) {
      // const config = await this.getReCaptchaConfig();

      return (
        await this.axios.post<Participant>('/participants', data /*, config */)
      ).data;
    }

    throw new Error('Already creating a participant.');
  }

  public async updateUser(data: UserPayload): Promise<User> {
    return (await this.axios.patch<User>('/users/me', data)).data;
  }

  public async submitScore(
    id: string,
    data: ParticipantScorePayload
  ): Promise<Participant> {
    return (
      await this.axios.patch<Participant>(`/participants/${id}/score`, data)
    ).data;
  }

  public async runLottery(id: string): Promise<Participant> {
    return (await this.axios.patch<Participant>(`/participants/${id}/lottery`))
      .data;
  }

  public async updateAddress(id: string, data: Address): Promise<Participant> {
    return (
      await this.axios.patch<Participant>(`/participants/${id}/address`, data)
    ).data;
  }

  /**
   * Campaign Endpoints:
   */
  public async getCampaignLeaderboard(
    id: string,
    params: CampaignGameLeaderboardParams
  ): Promise<CampaignGameLeaderboard> {
    return (
      await this.axios.get<CampaignGameLeaderboard>(
        `/campaign-games/${id}/leaderboard`,
        {
          params
        }
      )
    ).data;
  }

  private getCampaignSlug(country?: string): string {
    return `${import.meta.env.VITE_CAMPAIGN_BASE_SLUG}${
      (country && `-${country}`) || ''
    }`;
  }

  private async setCampaignData(user?: UserState): Promise<string> {
    try {
      const slug = this.getCampaignSlug();
      const campaign = await this.getCampaignBySlug(slug);
      Store.dispatch(setCampaignData(campaign));

      return campaign.id;
    } catch (error) {
      console.error('Error in setCampaignData:', error);
      throw error;
    }
  }

  private async getCampaignBySlug(slug: string): Promise<Campaign> {
    try {
      const response = await this.axios.get<Campaign>(`/campaigns/${slug}`);
      return response.data;
    } catch (error) {
      console.error('Error in getCampaignBySlug:', error);
      throw error;
    }
  }

  /**
   * Family Endpoints:
   */
  public async createFamily(data: FamilyCreatePayload): Promise<Family> {
    const { user } = Store.getState();
    // const config = await this.getReCaptchaConfig();
    const resp = await this.axios.post<Family>(
      '/editor/family',
      data /*, config */
    );

    Store.dispatch(setUserData({ ...user, ...{ family: resp.data } }));
    return resp.data;
  }

  public async updateFamily(name: string): Promise<Family> {
    // const config = await this.getReCaptchaConfig();

    const family = (
      await this.axios.patch<Family>('/editor/family', { name } /*, config */)
    ).data;

    Store.dispatch(setUserFamily(family));
    return family;
  }

  public async createAvatar(data: Partial<AvatarData>): Promise<Family> {
    // const config = await this.getReCaptchaConfig();

    const family = (
      await this.axios.post<Family>('/editor/avatars', data /*, config */)
    ).data;

    Store.dispatch(setUserFamily(family));
    return family;
  }

  public async getHeadshotUrl(avatarId: string): Promise<string[]> {
    const { signedUrl } = (
      await this.axios.get<SignedUrl>(
        `/editor/avatar/${avatarId}/headshot-image/signed-url`
      )
    ).data;

    return [signedUrl, signedUrl.slice(0, signedUrl.indexOf('?'))];
  }

  public async uploadHeadshot(url: string, headshot: File): Promise<void> {
    fetch(url, {
      headers: { 'Content-Type': 'image/png' },
      body: headshot,
      method: 'PUT'
    });
  }

  public async updateAvatar(data: Partial<AvatarData>): Promise<void> {
    // const config = await this.getReCaptchaConfig();

    const family = (
      await this.axios.patch<Family>(
        `/editor/avatars/${data.id}`,
        data /*, config */
      )
    ).data;

    Store.dispatch(setUserFamily(family));
  }

  public async deleteAvatar(id: string): Promise<void> {
    // const config = await this.getReCaptchaConfig();

    const family = (
      await this.axios.delete<Family>(`/editor/avatars/${id}` /*, config */)
    ).data;

    Store.dispatch(setUserFamily(family));
  }
}

export default new APIService();
